<script setup lang="ts">
import { computed } from 'vue'
import {PhX} from '@phosphor-icons/vue'
import type { Component } from 'vue'

interface Props {
  label: string
  variant?: 'default' | 'outline' | 'accent' | 'success' | 'warning' | 'danger'
  size?: 'small' | 'medium' | 'large'
  removable?: boolean
  icon?: Component
  media?: string
  mediaBgColor?: string
  mediaTextColor?: string
  clickable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  size: 'medium',
  removable: false,
  clickable: false,
})

const variantClasses = {
  default: 'bg-surface-2 text-ink-2 border border-line-2 hover:border-line-3 hover:bg-surface-3',
  outline: 'bg-transparent text-ink-4 border border-line-3 hover:bg-surface-2 hover:border-line-4',
  accent: 'bg-cyan-500/10 text-accent-cyan border border-cyan-500/20 hover:bg-cyan-500/20 hover:border-cyan-500/30',
  success: 'bg-emerald-500/10 text-accent-emerald border border-emerald-500/20 hover:bg-emerald-500/20 hover:border-emerald-500/30',
  warning: 'bg-amber-500/10 text-accent-amber border border-amber-500/20 hover:bg-amber-500/20 hover:border-amber-500/30',
  danger: 'bg-red-500/10 text-accent-red border border-red-500/20 hover:bg-red-500/20 hover:border-red-500/30',
}

const sizeClasses = {
  small: 'text-xs px-2.5 py-1 h-6',
  medium: 'text-sm px-3 py-1.5 h-7',
  large: 'text-base px-4 py-2 h-9',
}

const chipClasses = computed(() => [
  'inline-flex items-center gap-1.5 font-medium transition-all duration-200',
  'rounded-full',
  variantClasses[props.variant],
  sizeClasses[props.size],
  props.clickable ? 'cursor-pointer active:scale-[0.97]' : '',
  props.removable ? 'pr-1' : '',
])

const emit = defineEmits<{
  remove: []
  click: [event: MouseEvent]
}>()

function handleClick(e: MouseEvent) {
  if ((e.target as HTMLElement).closest('.chip-remove')) return
  emit('click', e)
}

function handleRemove(e: MouseEvent) {
  e.stopPropagation()
  emit('remove')
}
</script>

<template>
  <div
    :class="['chip', chipClasses]"
    :tabindex="props.clickable ? 0 : undefined"
    @keydown.enter.prevent="props.clickable && emit('click', $event as unknown as MouseEvent)"
    @keydown.space.prevent="props.clickable && emit('click', $event as unknown as MouseEvent)"
    @click="handleClick"
  >
    <slot name="media">
      <span
        v-if="props.media"
        :class="['w-5 h-5 rounded-full flex items-center justify-center shrink-0', props.mediaBgColor || 'bg-surface-3', props.mediaTextColor || 'text-ink-2']"
      >
        <component :is="props.media" class="w-3.5 h-3.5" />
      </span>
    </slot>

    <slot name="icon">
      <component v-if="props.icon" :is="props.icon" class="w-3.5 h-3.5" />
    </slot>

    <span class="truncate">{{ props.label }}</span>

    <button
      v-if="props.removable"
      type="button"
      class="chip-remove flex items-center justify-center w-5 h-5 -mr-1 ml-0.5 rounded-full hover:bg-tint-hover transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400"
      @click="handleRemove"
      aria-label="Remove"
    >
      <PhX class="w-3 h-3" />
    </button>
  </div>
</template>
